import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import {
  ColumnDefinitionV3,
  ColumnRendererComponentV3,
  FilterTableSettings,
  HierarchyTableV3Component,
  TableServiceV3,
} from '@gea/digital-ui-lib';
import { ProbablityOfFailureComponent } from './probability-of-failure.component';
import { TranslatePipe } from '@ngx-translate/core';
import { InfoBoxComponent } from '@brc/shared/components';
import { BRCBaseComponent } from '@brc/hrt-base-component';
import { FaultsApiService } from '@brc/shared/services';
import { LastInstanceRendererComponent } from '../table-config';
import { FaultsStore } from '@brc/shared/state';
import { PredictiveAlert, PredictiveAlertTree } from '@shared/models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { auditTime, distinctUntilChanged, filter } from 'rxjs';
import { isEqual } from 'lodash';
import { RootCauseButtonComponent } from './root-cause-btn.component';
import { sortTreeNodes } from '../table-config/table.util';
import { handleTableTreeFilters, invertSortDirectionsForClientSorting } from '@shared/helpers';

export const PREDICTIVE_ALERT_TABLE_FILTERS = 'PREDICTIVE_ALERT_TABLE_FILTERS';

interface ProbabilityOfFailure {
  probablity_of_failure: number;
  last_notified_probability: number;
}

function isPredictiveAlertTree(item: PredictiveAlert | PredictiveAlertTree): item is PredictiveAlertTree {
  return 'data' in item;
}

function setPredictionWindowToTwoWeeks(item: PredictiveAlert | PredictiveAlertTree): PredictiveAlertTree {
  const node = isPredictiveAlertTree(item) ? item : { data: item, children: [] };

  return {
    ...node,
    data: node.data ? { ...node.data, predictionWindowHours: '2 weeks' } : undefined,
    children: node.children?.map(setPredictionWindowToTwoWeeks),
  };
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gea-hrt-predictive-alerts',
  imports: [TranslatePipe, InfoBoxComponent, HierarchyTableV3Component],
  styleUrls: ['./predictive-alert.component.scss'],
  template: `
    <gea-hrt-info-box [boxTitle]="'PREDICTIVE_ALERT.TITLE' | translate" [withBorder]="true">
      @let data = parsedData();
      @if (data.length) {
        <div class="table-container-section">
          <geaui-hierarchy-table-v3
            [tableId]="TABLE_ID"
            class="table"
            [columnDefinitions]="columnDefinitions"
            [sortable]="true"
            [data]="data"
            [persistSettings]="false" />
        </div>
      } @else {
        @let dateRange = selectedDateRange();
        <p class="error-msg">
          {{
            'PREDICTIVE_ALERT.NO_DATA'
              | translate
                : {
                    dateRange: dateRange === 1 ? 24 : (dateRange ?? 0) / 7,
                  }
          }}

          {{ 'PREDICTIVE_ALERT.NO_DATA.DATE_UNIT.' + (dateRange === 1 ? 'HOUR' : 'WEEK') | translate }}
        </p>
      }
    </gea-hrt-info-box>
  `,
})
export class PredictiveAlertsComponent extends BRCBaseComponent implements OnInit {
  readonly TABLE_ID = 'predictive-alerts-table';
  public placeholder!: string;
  private readonly faultsApiService = inject(FaultsApiService);
  private readonly tableService = inject(TableServiceV3);
  readonly rowData = signal<PredictiveAlertTree[]>([]);
  readonly tableSettings = signal<FilterTableSettings>(this.getStoredSettings());
  private readonly faultsStore = inject(FaultsStore);
  readonly selectedDateRange = this.faultsStore.selectedDateRange;
  private readonly dateRangeAndMachines = computed(
    () => {
      const dateRange = this.faultsStore.selectedDateRange();
      const machines = this.faultsStore.selectedMachines();

      if (!dateRange || !machines) {
        return undefined;
      }

      return { dateRange, machines };
    },
    { equal: isEqual }
  );

  readonly parsedData = computed(() => {
    const tableSettings = invertSortDirectionsForClientSorting(this.tableSettings());
    const filteredData = handleTableTreeFilters<PredictiveAlert, PredictiveAlertTree>(tableSettings, this.rowData());
    return sortTreeNodes(tableSettings, filteredData);
  });

  columnDefinitions: ColumnDefinitionV3[] = [
    {
      key: 'machineName',
      displayName: 'PREDICTIVE_ALERT.MACHINE_NAME',
      width: 16,
    },
    {
      key: 'component',
      displayName: 'PREDICTIVE_ALERT.COMPONENT',
      width: 16,
    },
    {
      key: 'probability',
      displayName: 'PREDICTIVE_ALERT.PROBABLITY_OF_FAILURE',
      renderer: {
        component: ProbablityOfFailureComponent as ColumnRendererComponentV3<ProbabilityOfFailure>,
      },
      width: 16,
    },
    {
      key: 'scoredAt',
      displayName: 'PREDICTIVE_ALERT.ISSUED',
      renderer: {
        component: LastInstanceRendererComponent as ColumnRendererComponentV3<number>,
      },
      width: 16,
    },
    {
      key: 'predictive_alert_rootcauses',
      displayName: 'PREDICTIVE_ALERT.ROOTCAUSES',
      renderer: {
        component: RootCauseButtonComponent as ColumnRendererComponentV3<number>,
      },
      width: 16,
      sortable: false,
    },
    {
      key: 'predictionWindowHours',
      displayName: 'PREDICTIVE_ALERT.PREDICTION_WINDOW',
      width: 16,
      sortable: false,
    },
  ];

  constructor() {
    super();

    effect(() => {
      const selection = this.dateRangeAndMachines();

      if (!selection) {
        return;
      }

      const { dateRange, machines } = selection;

      if (!machines.length) {
        this.rowData.set([]);
      } else {
        this.faultsApiService
          .getPredictiveAlert(machines, dateRange)
          .pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean))
          .subscribe({
            next: (result) => {
              const nodes = result.map(setPredictionWindowToTwoWeeks);
              this.rowData.set(nodes);
            },
            error: () => {
              this.showError('error');
            },
          });
      }
    });
  }

  ngOnInit() {
    this.tableService
      .getFilterTableSettings(this.TABLE_ID)
      .pipe(
        auditTime(0),
        distinctUntilChanged((previous, current) => isEqual(previous.columns, current.columns)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((settings) => {
        const tableSettings = this.getEffectiveTableSettings(settings);
        this.tableSettings.set(tableSettings);
        localStorage.setItem(PREDICTIVE_ALERT_TABLE_FILTERS, JSON.stringify(tableSettings));
      });
  }

  private getStoredSettings(): FilterTableSettings {
    const storedFilters = localStorage.getItem(PREDICTIVE_ALERT_TABLE_FILTERS);
    if (!storedFilters) {
      return { columns: {} };
    }

    try {
      return JSON.parse(storedFilters) as FilterTableSettings;
    } catch {
      return { columns: {} };
    }
  }

  private getEffectiveTableSettings(settings: FilterTableSettings): FilterTableSettings {
    if (!Object.entries(settings.columns ?? {}).length) {
      const storedSettings = this.tableSettings();
      if (Object.entries(storedSettings.columns ?? {}).length) {
        return storedSettings;
      }
    }
    return settings;
  }
}
